/**
 * Solicitudes de aprobación
 *
 * El admin ve las de toda la empresa y las aprueba o rechaza; el vendedor ve
 * las suyas. Cualquiera de los dos puede cancelar una abierta, con nota.
 */

import { useEffect, useState } from 'react'
import { Check, X, Ban } from 'lucide-react'

import { t } from '@/i18n'
import { useAuth } from '@/hooks/useAuth'
import {
  OPEN_APPROVAL_STATUSES,
  useApprovalRequests,
  useApproveRequest,
  useCancelRequest,
  useMarkApprovalsSeen,
  useRejectRequest,
  type ApprovalRequest,
} from '@/hooks/useApprovals'
import { parseApiError } from '@/lib/utils'
import { ApprovalCard } from '@/components/approvals/ApprovalCard'
import { NoteDialog } from '@/components/approvals/NoteDialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/components/ui/toaster'

type Tab = 'open' | 'all'
type NoteAction = { kind: 'approve' | 'reject' | 'cancel'; request: ApprovalRequest }

const APPLIED_MESSAGE: Record<ApprovalRequest['kind'], string> = {
  discount: 'approvals.discountApplied',
  price_change: 'approvals.priceApplied',
  stock_out: 'approvals.stockOutApplied',
  annulment: 'approvals.annulmentApplied',
  devolution: 'approvals.devolutionApplied',
}

export function ApprovalsPage() {
  const { user, isAdmin, isAccountant } = useAuth()
  const { toast } = useToast()
  // El vendedor abre en "Todas": las respuestas (aprobadas, rechazadas) no están
  // entre las abiertas, y es lo que viene a ver cuando la campana le avisa.
  const [tab, setTab] = useState<Tab>(isAdmin || isAccountant ? 'open' : 'all')
  const [action, setAction] = useState<NoteAction | null>(null)

  const { data: requests = [], isLoading } = useApprovalRequests(
    tab === 'open' ? { status: OPEN_APPROVAL_STATUSES } : {},
    { poll: true }
  )
  const approve = useApproveRequest()
  const reject = useRejectRequest()
  const cancel = useCancelRequest()
  const isPending = approve.isPending || reject.isPending || cancel.isPending

  // Abrir la pantalla es ver las respuestas: dejan de contar en la campana.
  const markSeen = useMarkApprovalsSeen()
  const hasUnseen = requests.some((r) => r.unseen)
  useEffect(() => {
    if (hasUnseen && !markSeen.isPending) markSeen.mutate({})
  }, [hasUnseen])

  const onError = (error: unknown) =>
    toast({ title: t('common.error'), description: parseApiError(error), variant: 'destructive' })

  const confirm = async (note: string) => {
    if (!action) return
    const { kind, request } = action
    try {
      if (kind === 'approve') {
        const result = await approve.mutateAsync({ id: request.id, note: note || undefined })
        toast({ title: t(APPLIED_MESSAGE[result.kind]), variant: 'success' })
      } else if (kind === 'reject') {
        await reject.mutateAsync({ id: request.id, note })
        toast({ title: t('approvals.rejected') })
      } else {
        await cancel.mutateAsync({ id: request.id, note })
        toast({ title: t('approvals.cancelled') })
      }
      setAction(null)
    } catch (error) {
      onError(error)
    }
  }

  const actionsFor = (request: ApprovalRequest) => {
    if (isAccountant) return null
    // "Enviándose" solo la cierra un admin, por si el envío se cortó a la mitad.
    const isProcessing = request.status === 'processing'
    const isOpen = request.status === 'pending' || isProcessing
    if (!isOpen) return null
    const canCancel = isAdmin || (!isProcessing && request.requested_by_id === user?.id)
    return (
      <>
        {isAdmin && request.status === 'pending' && (
          <>
            <Button className="h-11" onClick={() => setAction({ kind: 'approve', request })}>
              <Check className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('approvals.approve')}
            </Button>
            <Button
              variant="outline"
              className="h-11 text-destructive hover:text-destructive"
              onClick={() => setAction({ kind: 'reject', request })}
            >
              <X className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('approvals.reject')}
            </Button>
          </>
        )}
        {canCancel && (
          <Button variant="ghost" className="h-11" onClick={() => setAction({ kind: 'cancel', request })}>
            <Ban className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('approvals.cancel')}
          </Button>
        )}
      </>
    )
  }

  const dialogConfig = action && {
    approve: { title: t('approvals.approve'), confirm: t('approvals.approve'), required: false, destructive: false },
    reject: { title: t('approvals.rejectTitle'), confirm: t('approvals.confirmReject'), required: true, destructive: true },
    cancel: { title: t('approvals.cancelTitle'), confirm: t('approvals.confirmCancel'), required: true, destructive: true },
  }[action.kind]

  return (
    <div className="space-y-4 w-full max-w-5xl">
      <div>
        <h1 className="text-xl md:text-2xl font-bold">{t('approvals.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {isAdmin || isAccountant ? t('approvals.subtitleAdmin') : t('approvals.subtitleSeller')}
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList className="w-full sm:w-auto h-11">
          <TabsTrigger value="open" className="flex-1 sm:flex-none h-9 px-6">{t('approvals.tabOpen')}</TabsTrigger>
          <TabsTrigger value="all" className="flex-1 sm:flex-none h-9 px-6">{t('approvals.tabAll')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : requests.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center">{t('approvals.empty')}</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2 items-start">
          {requests.map((request) => (
            <ApprovalCard key={request.id} request={request} actions={actionsFor(request)} />
          ))}
        </div>
      )}

      {dialogConfig && (
        <NoteDialog
          open={!!action}
          onOpenChange={(open) => !open && setAction(null)}
          title={dialogConfig.title}
          confirmLabel={dialogConfig.confirm}
          required={dialogConfig.required}
          destructive={dialogConfig.destructive}
          isPending={isPending}
          onConfirm={confirm}
        >
          {action && <ApprovalCard request={action.request} />}
        </NoteDialog>
      )}

    </div>
  )
}
